import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { GlobalConstants } from './constants/global-constants'
import { LoginComponent } from './components/auth/login/login.component'
import { AuthGuard } from './guards/auth.guard'
import { RouteGuard } from './guards/route.guard'
import { ActivityDashboardComponent } from './components/activity/dashboard/activity-dashboard.component'
import { CreateActivityComponent } from './components/activity/create-activity/create-activity.component'
import { GovernmentBusinessDashboardComponent } from './components/government-business/dashboard/government-business-dashboard.component'
import { CreateGovernmentBusinessComponent } from './components/government-business/create-government-business/create-government-business.component'
import { GovernmentBusinessSummaryComponent } from './components/government-business/summary/government-business-summary.component'
import { ComplianceDashboardComponent } from './components/compliance-dashboard/compliance-dashboard.component'
import { ActivitySummaryComponent } from './components/activity/summary/activity-summary.component'
import { CreateQueryComponent } from './components/query/create-query/create-query.component'
import { QueryDashboardComponent } from './components/query/query-dashboard/query-dashboard.component'
import { QueryDetailsComponent } from './components/query/query-details/query-details.component'
import { KnowledgeSearchComponent } from './components/knowledge/knowledge-search/knowledge-search.component'
import { KnowledgeQuickSelectComponent } from './components/knowledge/quick-select/knowledge-quick-select.component'
import { EditKnowledgeArticleComponent } from './components/knowledge/edit/edit-knowledge-article.component'
import { ViewKnowledgeArticleComponent } from './components/knowledge/view-knowledge-article/view-knowledge-article.component'
import { ActivityDetailsComponent } from './components/activity/activity-details/activity-details.component'
import { GovernmentBusinessDetailsComponent } from './components/government-business/government-business-details/government-business-details.component'
import { KnowledgeSummaryComponent } from './components/knowledge/knowledge-summary/knowledge-summary.component'
import { SummaryCalendarComponent } from './components/calendar-info/summary/summary-calendar.component'
import { CreateCalendarComponent } from './components/calendar-info/create-calendar/create-calendar.component'
import { DataRepositoryQuickSelectComponent } from './components/data-repository/quick-select/data-repository-quick-select.component'
import { DataRepositorySummaryComponent } from './components/data-repository/summary/data-repository-summary.component'
import { NewAnnouncementComponent } from './components/circular-communication/new-announcement/new-announcement.component'
import { AnnouncementSummaryComponent } from './components/circular-communication/summary/announcement-summary.component'
import { EmployeeSummaryComponent } from './components/employee-info/employee-summary/employee-summary.component'
import { QuerySummaryComponent } from './components/query/query-summary/query-summary.component'
import { ActivityPendingItemsComponent } from './components/activity/pending-items/activity-pending-items.component'
import { SettingsComponent } from './components/settings/settings.component'
import { ReportsComponent } from './components/reports/reports.component'
import { Form11Component } from './components/form11/form11.component'
import { OfflineTrackerSummaryComponent } from './components/offline-tracker/summary/offline-tracker-summary.component'
import { CreateNewOfflineTrackerComponent } from './components/offline-tracker/create/create-new-offline-tracker.component'
import { OfflineTrackerDetailsComponent } from './components/offline-tracker/details/offline-tracker-details.component'
import { OnboardingWizardComponent } from './components/onboarding-wizard/onboarding-wizard.component'
import { LandingComponent } from './components/auth/landing/landing.component'
import { loginOnlyRedirectGuard } from './guards/login-only-redirect.guard'
import { rootTenantRedirectGuard } from './guards/root-tenant-redirect.guard'
import { ModuleRouteGuard } from './guards/module-route.guard'

const routes: Routes = [
  {
    path: GlobalConstants.ROUTING.LOGIN,
    canActivate: [loginOnlyRedirectGuard],
    children: []
  },
  {
    path: GlobalConstants.ROUTING.LOGIN + '/' + GlobalConstants.ROUTING.COMPANY_DOMAIN,
    component: LoginComponent,
    canActivate: [RouteGuard]
  },
  {
    path: '',
    component: LandingComponent,
    canActivate: [rootTenantRedirectGuard]
  },
  {
    path: GlobalConstants.ROUTING.DASHBOARD,
    component: ComplianceDashboardComponent,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.DASHBOARD }
  },
  {
    path: GlobalConstants.ROUTING.ACTIVITY,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.ACTIVITY_TRACKER },
    children: [
      { path: GlobalConstants.ROUTING.DASHBOARD, component: ActivityDashboardComponent },
      { path: GlobalConstants.ROUTING.NEW, component: CreateActivityComponent },
      { path: GlobalConstants.ROUTING.SUMMARY, component: ActivitySummaryComponent },
      { path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.DETAILS}`, component: ActivityDetailsComponent },
      { path: GlobalConstants.ROUTING.PENDING, component: ActivityPendingItemsComponent }
    ]
  },
  {
    path: GlobalConstants.ROUTING.GOVERNMENT_BUSINESS,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.GOVERNMENT_BUSINESS_TASK },
    children: [
      { path: GlobalConstants.ROUTING.DASHBOARD, component: GovernmentBusinessDashboardComponent },
      { path: GlobalConstants.ROUTING.SUMMARY, component: GovernmentBusinessSummaryComponent },
      { path: GlobalConstants.ROUTING.NEW, component: CreateGovernmentBusinessComponent },
      {
        path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.DETAILS}`,
        component: GovernmentBusinessDetailsComponent
      }
    ]
  },
  {
    path: GlobalConstants.ROUTING.QUERY,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.QUERIES },
    children: [
      { path: GlobalConstants.ROUTING.DASHBOARD, component: QueryDashboardComponent },
      { path: GlobalConstants.ROUTING.SUMMARY, component: QuerySummaryComponent },
      {
        path: GlobalConstants.ROUTING.NEW,
        component: CreateQueryComponent,
        canActivate: []
      },
      {
        path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.DETAILS}`,
        component: QueryDetailsComponent
      }
    ]
  },
  {
    path: GlobalConstants.ROUTING.KNOWLEDGE_HUB,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.KNOWLEDGE_BANK },
    children: [
      { path: GlobalConstants.ROUTING.SEARCH, component: KnowledgeSearchComponent },
      { path: GlobalConstants.ROUTING.QUICK_SELECT, component: KnowledgeQuickSelectComponent },
      { path: GlobalConstants.ROUTING.LIST, component: KnowledgeSummaryComponent },
      {
        path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.VIEW}`,
        component: ViewKnowledgeArticleComponent
      },
      {
        path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.EDIT}`,
        component: EditKnowledgeArticleComponent
      },
      { path: GlobalConstants.ROUTING.ID, component: EditKnowledgeArticleComponent }
    ]
  },
  {
    path: GlobalConstants.ROUTING.CALENDAR_INFO,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.CALENDAR },
    children: [
      { path: GlobalConstants.ROUTING.SUMMARY, component: SummaryCalendarComponent },
      { path: GlobalConstants.ROUTING.NEW, component: CreateCalendarComponent },
      { path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.DETAILS}`, component: CreateCalendarComponent }
    ]
  },
  {
    path: GlobalConstants.ROUTING.DATA_REPOSITORY,
    canActivate: [AuthGuard],
    children: [
      { path: GlobalConstants.ROUTING.QUICK_SELECT, component: DataRepositoryQuickSelectComponent },
      {
        path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.SUMMARY}`,
        component: DataRepositorySummaryComponent
      }
    ]
  },
  {
    path: GlobalConstants.ROUTING.ANNOUNCEMENT,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.CIRCULAR_AND_COMMUNICATION },
    children: [
      { path: GlobalConstants.ROUTING.NEW, component: NewAnnouncementComponent },
      { path: GlobalConstants.ROUTING.SUMMARY, component: AnnouncementSummaryComponent },
      { path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.DETAILS}`, component: NewAnnouncementComponent }
    ]
  },
  {
    path: GlobalConstants.ROUTING.EMPLOYEE_INFO,
    canActivate: [AuthGuard],
    children: [{ path: GlobalConstants.ROUTING.SUMMARY, component: EmployeeSummaryComponent }]
  },
  {
    path: GlobalConstants.ROUTING.SETTINGS,
    canActivate: [AuthGuard],
    component: SettingsComponent
  },
  {
    path: GlobalConstants.ROUTING.REPORTS,
    canActivate: [AuthGuard],
    component: ReportsComponent
  },
  {
    path: `${GlobalConstants.ROUTING.FORM11_WORKFLOW}/${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.STATUS}/${GlobalConstants.ROUTING.PARAM_STATUS}/${GlobalConstants.ROUTING.PARAM_EMPLOYEE_ID}`,
    canActivate: [AuthGuard],
    component: Form11Component
  },
  {
    path: GlobalConstants.ROUTING.OFFLINE_TRACKER,
    canActivate: [AuthGuard, ModuleRouteGuard],
    data: { requiredModule: GlobalConstants.MODULE.OFFLINE_TRACKER },
    children: [
      { path: GlobalConstants.ROUTING.NEW, component: CreateNewOfflineTrackerComponent },
      { path: GlobalConstants.ROUTING.SUMMARY, component: OfflineTrackerSummaryComponent },
      {
        path: `${GlobalConstants.ROUTING.ID}/${GlobalConstants.ROUTING.DETAILS}`,
        component: OfflineTrackerDetailsComponent
      }
    ]
  },
  {
    path: 'settings/onboarding-wizard',
    canActivate: [AuthGuard],
    component: OnboardingWizardComponent
  }
]

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
